import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, Check, Gem, PenTool, Rocket } from "lucide-react";
import Reveal from "../components/Reveal";

const PACKAGES = [
  {
    id: "launch",
    icon: Rocket,
    name: "LAUNCH",
    price: "$3,500",
    big: "BUILD IT.",
    support: "You know what you want. We'll professionally build it.",
    bestFor: "Businesses with clear messaging, positioning and content that primarily need professional execution.",
    features: [
      "Up to 5 pages",
      "Responsive design",
      "Mobile optimization",
      "Contact / lead forms",
      "Basic SEO",
      "Analytics",
      "Professional development",
      "1 revision round",
    ],
    meta: [
      ["STRATEGY LEVEL", "Client-led"],
      ["STORY DEVELOPMENT", "Not included"],
      ["AT DIGITAL ROLE", "Execution"],
    ],
    cta: "Build My Website",
    testid: "pricing-launch-card",
    ctaTestid: "pricing-launch-cta",
    signature: false,
  },
  {
    id: "growth",
    icon: PenTool,
    name: "GROWTH",
    price: "$6,500",
    big: "HELP ME TELL IT.",
    support: "You know your business. We'll help sharpen the story and build the experience.",
    bestFor:
      "Established businesses that need stronger messaging, positioning and conversion strategy in addition to a professional website.",
    features: [
      "Everything in Launch",
      "Up to 10 pages",
      "Custom design",
      "Messaging refinement",
      "Story development",
      "Customer journey planning",
      "Conversion strategy",
      "Testimonials / case studies",
      "Booking integration",
      "Advanced analytics",
      "Basic workflow integration",
      "2 revision rounds",
    ],
    meta: [
      ["STRATEGY LEVEL", "Collaborative"],
      ["STORY DEVELOPMENT", "Guided"],
      ["AT DIGITAL ROLE", "Strategy + Execution"],
    ],
    cta: "Help Tell My Story",
    testid: "pricing-growth-card",
    ctaTestid: "pricing-growth-cta",
    signature: false,
  },
  {
    id: "signature",
    icon: Gem,
    name: "AT DIGITAL SIGNATURE",
    price: "$10,000+",
    priceLabel: "STARTING AT $10,000",
    badge: "THE COMPLETE AT DIGITAL EXPERIENCE",
    big: "UNDERSTAND MY BUSINESS. TELL IT FOR ME.",
    support: "You know your business better than anyone. We uncover the story your customers need to hear.",
    bestFor:
      "Established service businesses, high-ticket businesses, founders, professional firms and organizations that want AT Digital to deeply understand their company before determining what should be built.",
    featureIntro: "Everything in Growth",
    groups: [
      ["STRATEGY", ["Deep business discovery", "Founder / leadership interviews", "Customer analysis", "Competitive positioning review", "Value proposition development"]],
      ["STORY", ["Brand narrative development", "Core messaging", "Website storytelling architecture", "Proof / credibility strategy", "Customer journey development"]],
      ["EXPERIENCE", ["Premium custom design", "Full conversion architecture", "Mobile customer experience", "Interactive storytelling where appropriate"]],
      ["TECHNOLOGY", ["Custom development", "CRM integration", "Scheduling integration", "Payment integration where appropriate", "Custom intake systems", "Business workflow automation", "Custom tools / dashboards where appropriate"]],
      ["MEASUREMENT", ["Full-funnel analytics", "Conversion tracking", "Launch optimization", "Post-launch strategy session"]],
      ["SERVICE", ["Priority support", "3+ revision rounds"]],
    ],
    meta: [
      ["STRATEGY LEVEL", "AT Digital-led"],
      ["STORY DEVELOPMENT", "Comprehensive"],
      ["AT DIGITAL ROLE", "Strategic Partner"],
    ],
    microcopy: "Final scope is based on the complexity of your business and digital systems.",
    cta: "Tell Us Your Story",
    secondaryCta: "Book a Signature Consultation",
    testid: "pricing-signature-card",
    ctaTestid: "pricing-signature-cta",
    signature: true,
  },
];

const MetaRows = ({ meta, dark }) => (
  <div className={`mt-7 space-y-2.5 border-t pt-6 ${dark ? "border-navyline" : "border-slate-200"}`}>
    {meta.map(([k, v]) => (
      <div key={k} className="flex items-baseline justify-between gap-4">
        <span className={`font-mono text-[10px] font-semibold tracking-[0.16em] ${dark ? "text-slate-500" : "text-slate-400"}`}>
          {k}
        </span>
        <span className={`text-right text-sm font-bold ${dark ? "text-gold" : "text-ink"}`}>{v}</span>
      </div>
    ))}
  </div>
);

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="bg-[#F5F7FB] py-24 text-slate-700 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="font-mono text-xs font-semibold tracking-[0.3em] text-golddeep">WORK WITH AT DIGITAL</p>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            Choose how deep we go.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-500">
            Not three sizes of the same website — three levels of strategic involvement. Transparent starting points,
            defined deliverables, no surprises.
          </p>
        </Reveal>
        <div className="mt-20 grid grid-cols-1 items-stretch gap-8 lg:grid-cols-3 lg:gap-6 xl:gap-8">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12} className="h-full">
              <article
                data-testid={p.testid}
                className={`relative flex h-full flex-col rounded-3xl transition-transform duration-300 hover:-translate-y-1.5 ${
                  p.signature
                    ? "border-2 border-gold bg-navy p-9 text-slate-300 shadow-2xl shadow-gold/15 sm:p-11 lg:-translate-y-5 lg:scale-[1.04] lg:hover:-translate-y-7"
                    : "border border-slate-200 bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-9"
                }`}
              >
                {p.signature && (
                  <>
                    <div
                      className="pointer-events-none absolute inset-0 rounded-3xl"
                      style={{ background: "radial-gradient(80% 50% at 50% 0%, rgba(212,175,55,0.14), transparent)" }}
                      aria-hidden="true"
                    />
                    <span
                      data-testid="pricing-signature-badge"
                      className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold px-5 py-1.5 font-mono text-[10px] font-bold tracking-[0.16em] text-ink shadow-lg shadow-gold/30"
                    >
                      {p.badge}
                    </span>
                  </>
                )}
                <div className={`relative flex h-full flex-col ${p.signature ? "pt-3" : ""}`}>
                  <div className="flex items-center gap-4">
                    <p.icon className={`h-9 w-9 shrink-0 ${p.signature ? "text-gold" : "text-navy"}`} strokeWidth={1.5} />
                    <div>
                      <h3 className={`font-mono text-[11px] font-bold tracking-[0.22em] ${p.signature ? "text-gold" : "text-slate-500"}`}>
                        {p.name}
                      </h3>
                      <p className={`mt-1 font-display text-3xl font-extrabold tracking-tight ${p.signature ? "text-white" : "text-ink"}`}>
                        {p.price}
                      </p>
                    </div>
                  </div>
                  <p className={`mt-6 font-display text-xl font-extrabold leading-snug sm:text-2xl ${p.signature ? "text-goldlight" : "text-ink"}`}>
                    {p.big}
                  </p>
                  <p className={`mt-3 text-sm font-medium leading-relaxed ${p.signature ? "text-slate-300" : "text-slate-600"}`}>
                    {p.support}
                  </p>
                  <p className={`mt-3 text-[13px] leading-relaxed ${p.signature ? "text-slate-500" : "text-slate-500"}`}>
                    {p.bestFor}
                  </p>

                  {p.features && (
                    <ul className={`mt-7 space-y-3 border-t pt-7 ${p.signature ? "border-navyline" : "border-slate-200"}`}>
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-sm">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                          <span className="text-slate-600">{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {p.groups && (
                    <div className="mt-7 flex-1 border-t border-navyline pt-7">
                      <p className="flex items-start gap-3 text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                        <span className="font-semibold text-slate-200">{p.featureIntro}</span>
                      </p>
                      {p.groups.map(([label, items]) => (
                        <div key={label} className="mt-5">
                          <p className="font-mono text-[10px] font-bold tracking-[0.22em] text-gold">{label}</p>
                          <ul className="mt-2.5 space-y-2">
                            {items.map((f) => (
                              <li key={f} className="flex items-start gap-3 text-sm">
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                                <span className="text-slate-200">{f}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  <MetaRows meta={p.meta} dark={p.signature} />

                  {p.signature && (
                    <p className="mt-6 font-mono text-[11px] font-bold tracking-[0.2em] text-slate-400">
                      PRICE: <span className="text-gold">{p.priceLabel}</span>
                    </p>
                  )}

                  <div className="mt-auto pt-8">
                    <Link
                      to={`/start?package=${p.id}`}
                      data-testid={p.ctaTestid}
                      className={`group inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-bold transition-colors duration-300 ${
                        p.signature
                          ? "bg-gold text-ink shadow-lg shadow-gold/25 hover:bg-goldlight"
                          : "border border-navy/25 text-navy hover:border-gold hover:text-golddeep"
                      }`}
                    >
                      {p.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                    {p.secondaryCta && (
                      <Link
                        to="/contact?book=1"
                        data-testid="pricing-signature-consultation-cta"
                        className="group mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-gold/50 px-7 py-3.5 text-sm font-semibold text-gold transition-colors duration-300 hover:bg-gold/10"
                      >
                        <CalendarCheck className="h-4 w-4" />
                        {p.secondaryCta}
                      </Link>
                    )}
                    {p.microcopy && (
                      <p data-testid="pricing-signature-microcopy" className="mt-4 text-center font-mono text-[10px] font-medium leading-relaxed tracking-[0.08em] text-slate-500">
                        {p.microcopy}
                      </p>
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
